import React from 'react';
import { css, cx } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import { useNode } from '@craftjs/core';
import { useRenderContext } from '../../context/RenderContext';
import { MAX_PADDING, PAGE_NODE_NAME } from '../../utils/constants';
import {
  computeBackgroundImageStyle,
  getBgImageScaleProps,
  getGlobalStyleColor,
  getMediaQueryHeader,
  toCSSSpacingString,
} from '../../utils/styleHelpers';
import type { TRBLNumberCOORDS } from '../../types/layout';
import type { NodeCustomProps } from '../../types/craft';
import type { StylesConfig } from '../../types/styles';
import { PagePropsDto } from '../../dto/craft/props/PagePropsDto';
import { getContinuousAnimationStyle } from '../../utils/continuousAnimationStyles';
import { getBgVideoEmbedSrc } from '../../utils/videoHelpers';
import {
  getBackgroundVideoContainerStyle,
  getBackgroundVideoThumbnailStyle,
  getBackgroundVideoIframeStyle,
} from '../../utils/backgroundVideoStyles';

export type PageProps = PagePropsDto & {
  children?: React.ReactNode;
};

export const defaultProps: Partial<PageProps> = {
  fillSpace: 'yes',
  padding: [20, 20, 20, 20] as [number, number, number, number],
  backgroundColor: { r: 255, g: 255, b: 255, a: 1 },
  radius: 0,
  maxWidth: '100%',
  height: 'auto',
  gap: 10,
  bgImage: null,
  bgImageScale: 100,
  bgImageOpacity: 100,
  bgImageMode: 'fill',
  bgImagePosition: 'center center',
  bgVideoSourceType: 'url',
  bgVideoUrl: undefined,
  bgVideoProvider: undefined,
  bgVideoThumbnailUrl: undefined,
  bgVideoEmbedCode: undefined,
  bgVideoMode: 'fill',
  bgVideoOpacity: 100,
  bgVideoAutoPlay: true, //not used in inspector, needed for thumbnail generation in builder mode for sections
  tablet: {},
  mobile: {},
};

const computeRootStyle = (props: Partial<PageProps>, activeGlobalStyles: StylesConfig) => {
  const { backgroundColor, padding, radius } = props;
  const background = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);

  return {
    background,
    padding: padding ? `${toCSSSpacingString(padding)}` : undefined,
    borderRadius: radius ? `${radius}px` : undefined,
  };
};
const rootStyle = () => {
  return css({
    width: '100%',
    height: '100%',
    containerType: 'inline-size',
  });
};
const contentRootStyle = (
  props: Partial<PageProps>,
  isServerRendering: boolean,
  activeGlobalStyles: StylesConfig,
) => {
  const desktopBpStyle = computeRootStyle(
    {
      ...props,
      padding: props.padding || [0, 0, 0, 0],
      radius: props.radius || 0,
      backgroundColor: props.backgroundColor || { r: 255, g: 255, b: 255, a: 1 },
    },
    activeGlobalStyles,
  );

  const finalStyle: CSSInterpolation = {
    ...(desktopBpStyle as CSSObject),
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
  };

  const { mobile, tablet } = props;
  if (mobile) {
    let padding = mobile.padding || props.padding;
    //ensure we have padding guardrails

    padding = clampPadding(padding as TRBLNumberCOORDS, MAX_PADDING['mobile']);
    //ensure to have background props in the mobile style
    const mobileStyles = computeRootStyle(
      { ...mobile, padding } as Partial<PageProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    let padding = tablet.padding || props.padding;
    //ensure we have padding guardrails
    padding = clampPadding(padding as TRBLNumberCOORDS, MAX_PADDING['tablet']);
    //ensure to have background props in the tablet style
    const tabletStyles = computeRootStyle(
      { ...tablet, padding } as Partial<PageProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
};

const backgroundImageStyle = (props: Partial<PageProps>, isServerRendering: boolean) => {
  const currentBpStyle = computeBackgroundImageStyle({
    ...props,
    bgImageScale: props.bgImageScale || 100,
    bgImageOpacity: props.bgImageOpacity || 100,
    bgImageMode: props.bgImageMode || 'fill',
    bgImagePosition: props.bgImagePosition || 'center center',
  });

  const continuousStyle = getContinuousAnimationStyle(
    props.bgContinuousAnimation,
    props.bgContinuousSpeed,
    props.bgContinuousIntensity,
  );

  const finalStyle: CSSInterpolation = {
    ...currentBpStyle,
    ...continuousStyle,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundBlendMode: 'normal',
    zIndex: 0,
    pointerEvents: 'none',
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const imageScaleProps = getBgImageScaleProps(props, mobile as Partial<PageProps>);
    //make sure to have these both imagescale and bgImageMode props if either of them are available in detach state
    const mobileStyles = computeBackgroundImageStyle({
      ...imageScaleProps,
      ...mobile,
    } as Partial<PageProps>);
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = mobileStyles;
  }

  if (tablet) {
    const imageScaleProps = getBgImageScaleProps(props, tablet as Partial<PageProps>);
    //make sure to have these both imagescale and bgImageMode props if either of them are available in detach state
    const tabletStyles = computeBackgroundImageStyle({
      ...imageScaleProps,
      ...tablet,
    } as Partial<PageProps>);
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletStyles;
  }

  return css(finalStyle);
};

const computeContentStyle = (props: Partial<PageProps>) => {
  const { maxWidth, gap } = props;
  const maxWidthValue = typeof maxWidth === 'string' && maxWidth === '100%' ? 'none' : maxWidth;
  return {
    maxWidth: maxWidth ? maxWidthValue : undefined,
    gap: gap ? `${gap}px` : undefined,
  };
};

const contentStyle = (props: Partial<PageProps>, isServerRendering: boolean) => {
  const currentBpStyle = computeContentStyle(props);
  const finalStyle: CSSInterpolation = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    ...currentBpStyle,
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeContentStyle(mobile as Partial<PageProps>);
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = mobileStyles;
  }
  if (tablet) {
    const tabletStyles = computeContentStyle(tablet as Partial<PageProps>);
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletStyles;
  }

  finalStyle['& > *:not(dialog)'] = {
    width: '100%!important',
    flex: 'none!important',
  };
  return css(finalStyle);
};

const Page = (props: Partial<PageProps>) => {
  const mergedProps = {
    ...defaultProps,
    ...props,
  };

  const { isEditable, environment, styles: activeGlobalStyles } = useRenderContext();

  const {
    connectors: { connect, drag },
  } = useNode();

  const {
    children,
    bgVideoSourceType,
    bgVideoUrl,
    bgVideoEmbedCode,
    bgVideoThumbnailUrl,
    bgVideoAutoPlay,
  } = mergedProps;
  const isServerRendering = environment === 'server';
  const isBuilderMode = environment === 'builder';

  const bgVideoEmbedUrl = getBgVideoEmbedSrc(bgVideoSourceType, bgVideoUrl, bgVideoEmbedCode, {
    autoplay: bgVideoAutoPlay,
  });
  const hasBackgroundVideo = Boolean(bgVideoEmbedUrl);

  return (
    <div
      className={cx('page-node', isEditable && 'boundary-indicator', rootStyle())}
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
        }
      }}
    >
      <div className={contentRootStyle(mergedProps, isServerRendering, activeGlobalStyles)}>
        {mergedProps.bgImage &&
        mergedProps.bgContinuousAnimation &&
        mergedProps.bgContinuousAnimation !== 'none' ? (
          <div
            className={css({
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 0,
            })}
          >
            <div className={backgroundImageStyle(mergedProps, isServerRendering)} />
          </div>
        ) : (
          mergedProps.bgImage && (
            <div className={backgroundImageStyle(mergedProps, isServerRendering)} />
          )
        )}
        {hasBackgroundVideo && (
          <div className={getBackgroundVideoContainerStyle({}, isServerRendering)}>
            {isBuilderMode ? (
              <div className={getBackgroundVideoThumbnailStyle(mergedProps, bgVideoThumbnailUrl)} />
            ) : (
              <iframe
                src={bgVideoEmbedUrl || undefined}
                className={getBackgroundVideoIframeStyle(mergedProps)}
                allow={`accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                title="Background video"
              />
            )}
          </div>
        )}
        <div className={cx('page-content', contentStyle(mergedProps, isServerRendering))}>
          {children}
        </div>
      </div>
    </div>
  );
};

Page.craft = {
  displayName: PAGE_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: 'Landing Page',
    isLocked: false,
  } as NodeCustomProps,
};

export default Page;

export function clampPadding(padding: TRBLNumberCOORDS, maxPadding: number): TRBLNumberCOORDS {
  return padding.map((p) => Math.min(p, maxPadding)) as TRBLNumberCOORDS;
}
